import { css } from 'styled-components';
import { PxToRem, PX_TO_REM } from '../sizes/sizes';
import { CSSProperties } from 'react';
import { EORA_NUMBERS } from '../numbers/eoraNumbers';
import { IGapPropsBase, IGutterAsMarginPropsBase, useFlexDirectionCss } from './spaceCommon';

export const SPACE_DEFAULT = 24 as const;
export type Space =
  | typeof SPACE_DEFAULT
  | 2
  | 4
  | 8
  | 10
  | 12
  | 16
  | 18
  | 20
  | 24
  | 32
  | 40
  | 48
  | 60
  | 64
  | 80
  | 96
  | 112
  | 128
  | 144
  | 160
  | 176
  | 192
  | 208
  | 224
  | 240
  | 256;

export const spaces: Record<Space, PxToRem> = {
  2: PX_TO_REM[2], // Note: exemption from DD
  4: PX_TO_REM[4],
  8: PX_TO_REM[8],
  10: PX_TO_REM[10], // Note: exemption from DD
  12: PX_TO_REM[12],
  16: PX_TO_REM[16],
  18: PX_TO_REM[18],
  20: PX_TO_REM[20],
  24: PX_TO_REM[24],
  32: PX_TO_REM[32],
  40: PX_TO_REM[40],
  48: PX_TO_REM[48],
  60: PX_TO_REM[60],
  64: PX_TO_REM[64],
  80: PX_TO_REM[80],
  96: PX_TO_REM[96],
  112: PX_TO_REM[112],
  128: PX_TO_REM[128],
  144: PX_TO_REM[144],
  160: PX_TO_REM[160],
  176: PX_TO_REM[176],
  192: PX_TO_REM[192],
  208: PX_TO_REM[208],
  224: PX_TO_REM[224],
  240: PX_TO_REM[240],
  256: PX_TO_REM[256]
};

export const EORA_SPACES_STANDARD = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'xlarge'
} as const;

export const EORA_SPACES_EXTRA_LARGE = {
  X2LARGE: 'x2large',
  X3LARGE: 'x3large',
  X4LARGE: 'x4large',
  X8LARGE: 'x8large'
} as const;

export const EORA_SPACES_OFFGRID = {
  OFFGRID_1: 'offgrid_1',
  OFFGRID_3: 'offgrid_3',
  OFFGRID_6: 'offgrid_6',
  OFFGRID_9: 'offgrid_9',
  OFFGRID_10: 'offgrid_10',
  OFFGRID_12: 'offgrid_12',
  OFFGRID_20: 'offgrid_20'
} as const;

export const EORA_SPACES_SPECIAL = {
  FORMS_INPUT_AFTER: 'forms_input_after',
  TABLE_CELL_PADDING: 'table_cell_padding',
  TABLE_CELL_PADDING_COMPACT: 'table_cell_padding_compact'
} as const;

export const EORA_SPACES = {
  ...EORA_SPACES_STANDARD,
  ...EORA_SPACES_EXTRA_LARGE,
  ...EORA_SPACES_OFFGRID,
  ...EORA_SPACES_SPECIAL
} as const;

export const EORA_SPACE_DEFAULT = EORA_SPACES_STANDARD.XLARGE;
export type EoraSpaces = typeof EORA_SPACES;
export type EoraSpace = EoraSpaces[keyof EoraSpaces];

export const eoraSpaces: Record<EoraSpace, PxToRem> = {
  [EORA_SPACES.OFFGRID_1]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_OFFGRID_1],
  [EORA_SPACES.XSMALL]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_XSMALL],
  [EORA_SPACES.OFFGRID_3]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_OFFGRID_3],
  [EORA_SPACES.SMALL]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_SMALL],
  [EORA_SPACES.OFFGRID_6]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_OFFGRID_6],
  [EORA_SPACES.MEDIUM]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_MEDIUM],
  [EORA_SPACES.OFFGRID_9]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_OFFGRID_9],
  [EORA_SPACES.OFFGRID_10]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_OFFGRID_10],
  [EORA_SPACES.OFFGRID_12]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_OFFGRID_12],
  [EORA_SPACES.LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_LARGE],
  [EORA_SPACES.OFFGRID_20]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_OFFGRID_20],
  [EORA_SPACES.XLARGE]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_XLARGE],
  [EORA_SPACES.X2LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_2XLARGE],
  [EORA_SPACES.FORMS_INPUT_AFTER]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.FORMS_INPUT_SPACING_AFTER],
  [EORA_SPACES.X3LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_3XLARGE],
  [EORA_SPACES.X4LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_4XLARGE],
  [EORA_SPACES.X8LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.SPACING_8XLARGE],
  [EORA_SPACES.TABLE_CELL_PADDING]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.TABLE_CELL_SPACING_PADDING],
  [EORA_SPACES.TABLE_CELL_PADDING_COMPACT]: PX_TO_REM[EORA_NUMBERS.CORE.SPACING.TABLE_CELL_SPACING_PADDING_COMPACT]
};

/**
 * @deprecated EORA V2 AllowableGutter should be dynamic based on screen size
 */
export type AllowableGutter = typeof SPACE_DEFAULT | 2 | 4 | 8 | 10 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 64 | 128;
export type AllowableGutterEora = EoraSpace;

/**
 * @deprecated EORA V2 AllowableGutter should be dynamic based on screen size
 */
export type IAllowableGutterLegacyProps = boolean | AllowableGutter;
export type IAllowableGutterProps = IAllowableGutterLegacyProps | AllowableGutterEora;

export interface IGutterAsMarginProps extends IGutterAsMarginPropsBase<IAllowableGutterProps> {}

export interface IGutterProps extends IGutterAsMarginProps {
  gutterAsMargin?: boolean;
}

export interface IPadding extends Partial<Record<'top' | 'right' | 'bottom' | 'left', AllowableGutterEora | AllowableGutter>> {}

/**
 * Backward compatible size selector that handles AllowableGutter and AllowableGutterEora
 */
const getSpaceValue = (space: IAllowableGutterProps) => {
  if (space === true) return eoraSpaces[EORA_SPACE_DEFAULT];
  if (Object.values(EORA_SPACES).includes(space as AllowableGutterEora)) return eoraSpaces[space as AllowableGutterEora];
  if (typeof space === 'number') return spaces[space];
  return '0';
};

export const computeSpace = (
  assignedSpace?: IAllowableGutterProps,
  parentSpace?: IAllowableGutterProps,
  defaultSpace?: AllowableGutter | AllowableGutterEora
) => {
  if (assignedSpace === false) return `0`;
  if (assignedSpace) return getSpaceValue(assignedSpace);
  if (parentSpace === false) return `0`;
  if (parentSpace) return getSpaceValue(parentSpace);
  if (defaultSpace) return getSpaceValue(defaultSpace);
  return `0`;
};

const getGutterSpace = (defaultPadding?: IPadding, gutters?: IGutterProps) => {
  const spaceTop = computeSpace(gutters?.gutterTop, gutters?.gutter, defaultPadding?.top);
  const spaceRight = computeSpace(gutters?.gutterRight, gutters?.gutter, defaultPadding?.right);
  const spaceBottom = computeSpace(gutters?.gutterBottom, gutters?.gutter, defaultPadding?.bottom);
  const spaceLeft = computeSpace(gutters?.gutterLeft, gutters?.gutter, defaultPadding?.left);
  return `${spaceTop} ${spaceRight} ${spaceBottom} ${spaceLeft}`;
};

export const useGutterMarginOrPaddingCss = (paddingMaringValues: string, asMargin?: boolean) => css<IGutterProps>`
  ${({ gutterAsMargin }) =>
    !gutterAsMargin &&
    !asMargin &&
    css`
      margin: 0;
    `}
  // Priorities (higher number gets higher prio)
    // 1. Gutter - surrounding space
    // 2. Gutter* - individual spacing
    ${({ gutterAsMargin }) => css`
    ${gutterAsMargin || (asMargin && gutterAsMargin === undefined) ? 'margin' : 'padding'}: ${paddingMaringValues};
  `}
`;

export const useGutterCss = (defaultPadding?: IPadding, asMargin?: boolean) => css<IGutterProps>`
  ${({ gutterTop, gutterLeft, gutterBottom, gutter, gutterRight }) =>
    useGutterMarginOrPaddingCss(
      getGutterSpace(defaultPadding, {
        gutterTop,
        gutterLeft,
        gutterBottom,
        gutter,
        gutterRight
      }),
      asMargin
    )};
`;

export const useGutterAsMarginCss = (defaultPadding?: IPadding) => useGutterCss(defaultPadding, true);

export const DEFAULT_INPUT_BOTTOM_PAD = 8;

export interface IGapSizeProps extends Pick<CSSProperties, 'alignItems'> {
  gap?: boolean | AllowableGutter | AllowableGutterEora;
}

export const useGapCss = (space: AllowableGutter | AllowableGutterEora = EORA_SPACE_DEFAULT) => css<IGapSizeProps>`
  display: flex;

  ${({ gap }) => {
    return (
      gap !== false &&
      css`
        gap: ${gap === true ? getSpaceValue(space) : getSpaceValue(gap ?? space)};
      `
    );
  }};

  ${({ alignItems }) =>
    alignItems &&
    css`
      align-items: ${alignItems};
    `};
`;

export const useColumnGapCss = (space: AllowableGutter | AllowableGutterEora = EORA_SPACE_DEFAULT) => css<IGapSizeProps>`
  flex-direction: column;
  ${useGapCss(space)}
`;

export interface IGapProps extends IGapPropsBase, IGapSizeProps {}

export const useCommonGapsCss = (space: AllowableGutter | AllowableGutterEora = EORA_SPACE_DEFAULT) => css<IGapProps>`
  ${({ column }) => (column ? useColumnGapCss(space) : useGapCss(space))}
  ${useFlexDirectionCss()}
`;
